/**
 * หน้าจอความล้มเหลวของการตามสอบ Lot — หนึ่งสถานะ หนึ่งข้อความ
 *
 * กรณีที่สำคัญที่สุดของหน้าจอนี้คือ **`404`** และมันต้องอ่านออกว่า "ไม่พบ lot นี้
 * ในมุมมองของคุณ" ไม่ใช่ "ระบบผิดพลาด": contract รวมสองความหมายไว้ในคำตอบเดียว
 * โดยตั้งใจ — lot ที่ไม่มีอยู่จริง กับ lot ที่มีอยู่แต่อยู่นอกโรงงานที่ผู้เรียกมีสิทธิ์
 * เห็น — เพราะ lot ถูกอ้างด้วย id ลำพัง การแยกสองกรณีจะเท่ากับยืนยันการมีอยู่ของ
 * lot ในโรงงานอื่นให้ใครก็ตามที่อ่านเลขจากป้ายมาได้ หน้าจอจึงต้องไม่เดาแทนผู้ใช้ว่า
 * เป็นกรณีไหน แต่ต้องบอกให้ครบทั้งสองทาง ไม่งั้นผู้ใช้จะสรุปว่า lot นั้นไม่มีอยู่จริง
 *
 * และ `403` ที่นี่ **ไม่ใช่เรื่องของโรงงาน** ต่างจากกลุ่มรับเข้าสต๊อก: ขอบเขตโรงงาน
 * ของ endpoint นี้ตอบ `404` ไปแล้ว สิ่งที่เหลือให้ `403` แปลได้อย่างเดียวคือบัญชีนี้
 * ไม่มีสิทธิ์ `INVENTORY` + VIEW เลย ซึ่งแก้ด้วยการขอสิทธิ์ ไม่ใช่ด้วยการเปลี่ยน lot
 */

import { StatusBadge } from '../StatusBadge';
import type {
  ApiErrorEnvelope,
  LotGenealogyFailure,
} from '../../lib/lotTraceability/client';
import { INVENTORY_MODULE } from '../../lib/lotTraceability/types';

export interface LotGenealogyFailureNoticeProps {
  readonly failure: LotGenealogyFailure;
  /** lot ที่คำขอนี้อ้างถึง — `404` อ่านรู้เรื่องขึ้นมากเมื่อบอกว่า lot ไหน */
  readonly lotId: string;
  readonly testId: string;
  readonly onRetry?: () => void;
}

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function envelopeOf(failure: LotGenealogyFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็น
 * ผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(failure: LotGenealogyFailure, lotId: string): FailureCopy {
  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return {
        heading: 'สิทธิ์ของคุณไม่ครอบคลุมการอ่านข้อมูลสต๊อก',
        text: `เซิร์ฟเวอร์ตอบ 403 — การตามสอบ lot ถูก gate ด้วยโมดูล ${INVENTORY_MODULE} และสิทธิ์ดู ไม่ใช่โมดูลของตัวเอง (D-073) ขอสิทธิ์ดูข้อมูลสต๊อกกับผู้ดูแลระบบ การเปลี่ยนเลข lot จะไม่ช่วยอะไร`,
      };
    case 'not-found':
      return {
        heading: `ไม่พบ lot ${lotId} ในมุมมองของคุณ`,
        text: 'เซิร์ฟเวอร์ตอบ 404 — ระบบไม่ยืนยันว่า lot นี้ไม่มีอยู่จริง หรือมีอยู่แต่อยู่ในโรงงานที่คุณไม่มีสิทธิ์เห็น สองอย่างนี้ตอบเหมือนกันโดยตั้งใจ (D-004) ตรวจว่าเลขที่กรอกตรงกับป้ายหรือไม่ ถ้าตรงและยังไม่พบ ให้ถามผู้ดูแลระบบว่า lot นี้อยู่ในโรงงานใด',
      };
    case 'bad-request':
      return {
        heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
        text: 'เซิร์ฟเวอร์ตอบ 400 — รูปแบบของ lot id หรือของ cursor หน้าถัดไปไม่ตรงกับที่ API รับ ลองเปิด lot นี้ใหม่จากช่องค้นหาแทนการกดโหลดหน้าถัดไปซ้ำ',
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลความสัมพันธ์จริงมาแสดง — และ **ห้ามตีความว่า lot นี้ไม่มีความสัมพันธ์**',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: `${failure.detail} — หน้าจอนี้จึงไม่มีความสัมพันธ์ของ lot จะแสดง และจะไม่เดาข้อมูลขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงสายความสัมพันธ์ที่อ่านได้ไม่ครบ ดีกว่าแสดงรายการที่ขาดไปบางบรรทัดแล้วมีคนสรุปว่าตามของครบแล้วทั้งที่ยังเหลืออยู่ในมือลูกค้า',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

export function LotGenealogyFailureNotice({
  failure,
  lotId,
  testId,
  onRetry,
}: LotGenealogyFailureNoticeProps) {
  const copy = failureCopy(failure, lotId);
  const serverMessage = envelopeOf(failure)?.message ?? null;

  return (
    <div
      className="cap-notice"
      data-testid={testId}
      data-failure={failure.status}
      role="status"
    >
      <StatusBadge tone="danger">อ่านข้อมูลไม่สำเร็จ</StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid={`${testId}-message`}>
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}
